import React, { useMemo } from 'react'

import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import useTourFacetLocationCategory from 'hooks/TourV2/useTourFacetLocationCategory'
import { TOURS_GROUP_TYPE, TOURS_GROUP_TYPES_LIST } from 'constants/tours'
import config from 'constants/config'
import TourPageTailorMadeTourCategoryTile from 'components/Tours/TourPageTailorMadeTourCategoryTile'
import TourPageSearchTourCategoryTile from 'components/Tours/TourPageSearchTourCategoryTile'
import { LUXURY_ESCAPES } from 'constants/brands'

const CATEGORIES: Array<App.Tours.CategoryData> = [
  {
    id: 'signature_series',
    name: 'Signature Series',
    subText: 'An experience like no other',
    imageId: 'tour-category-tile-signature-serie',
    show: config.BRAND === LUXURY_ESCAPES,
    parameterName: 'link',
    parameterValue: '/campaign/signature-series',
  },
  {
    id: 'tailor_made_tours',
    name: 'Tailor Made Tours',
    subText: '',
    imageId: 'tour-category-tile-tailor-made-tour',
    show: config.TAILOR_MADE_TOURS_ENABLED,
  },
  {
    id: 'deluxe_tours',
    name: 'Deluxe Tours',
    subText: 'Discover the highlights, effortlessly',
    imageId: 'tour-category-tile-deluxe',
    show: true,
    parameterName: 'tourStyles',
    parameterValue: ['Deluxe'],
  },
  {
    id: 'premium_tours',
    name: 'Premium Tours',
    subText: 'Elevated experiences, elevated accommodation',
    imageId: 'tour-category-tile-premium-v2',
    show: true,
    parameterName: 'tourStyles',
    parameterValue: ['Premium'],
  },
  {
    id: 'ultra_lux_tours',
    name: 'Ultra Lux Tours',
    subText: 'Only the best, from start to finish',
    imageId: 'tour-category-tile-ultra-lux-v2',
    show: true,
    parameterName: 'tourStyles',
    parameterValue: ['Ultralux'],
  },
  {
    id: 'private_journeys',
    name: 'Private Journeys',
    subText: 'An intimate journey with just you and your partner',
    imageId: 'tour-category-tile-private-journey',
    show: true,
    parameterName: TOURS_GROUP_TYPE.isPrivateRequest,
    parameterValue: true,
  },
  {
    id: 'food_and_culinary',
    name: 'Food & Culinary',
    subText: 'Taste your way around the world',
    imageId: 'tour-category-tile-food-alt',
    show: false,
    parameterName: 'categories',
    parameterValue: ['foodandculinarytours'],
  },
  {
    id: 'rail_journeys',
    name: 'Rail Journeys',
    subText: 'All aboard to see the sights',
    imageId: 'tour-category-tile-rail-journeys',
    show: false,
    parameterName: 'categories',
    parameterValue: ['railjourneys'],
  },
  {
    id: 'cultural',
    name: 'Cultural',
    subText: 'See the sights & dive into culture',
    imageId: 'tour-category-tile-cultural',
    show: false,
    parameterName: 'categories',
    parameterValue: ['culturaltours'],
  },
  {
    id: 'widlife_safari',
    name: 'Wildlife Safari',
    subText: 'Roam amongst diverse landscapes & wildlife',
    imageId: 'tour-category-tile-wildlife',
    show: false,
    parameterName: 'categories',
    parameterValue: ['wildlifesafaris'],
  },
  {
    id: 'lux_exclusive',
    name: 'Lux Exclusive',
    subText: 'Exclusive to Luxury Escapes',
    imageId: 'tour-category-tile-lux-exclusive',
    show: false,
    parameterName: 'categories',
    parameterValue: ['luxexclusive'],
  },
  {
    id: 'family_friendly',
    name: 'Family Friendly',
    subText: 'Adventures for the whole family',
    imageId: 'tour-category-tile-family',
    show: false,
    parameterName: TOURS_GROUP_TYPE.isFamilyFriendly,
    parameterValue: true,
  },
  {
    id: 'hiking_cycling_adventure',
    name: 'Hiking, Cycling & Adventure',
    subText: 'Explore the great outdoors',
    imageId: 'tour-category-tile-adventure',
    show: false,
    parameterName: 'categories',
    parameterValue: ['hikingcyclingandadventure'],
  },
  {
    id: 'festivals_and_events',
    name: 'Festivals & Events',
    subText: "Don't miss out on life's best moments",
    imageId: 'tour-category-tile-festival',
    show: false,
    parameterName: 'categories',
    parameterValue: ['festivalsandevents'],
  },
  {
    id: 'sailing',
    name: 'Sailing',
    subText: 'Hop on board to explore the seas',
    imageId: 'tour-category-tile-sailing',
    show: false,
    parameterName: 'categories',
    parameterValue: ['sailing'],
  },
  {
    id: 'health_and_wellness',
    name: 'Health & Wellness',
    subText: 'Relax & rejuvenate',
    imageId: 'tour-category-tile-health',
    show: false,
    parameterName: 'categories',
    parameterValue: ['healthandwellness'],
  },
]

interface Props {
  title?: React.ReactNode;
}

function TourPageCategoriesCarousel(props: Props) {
  const { title } = props

  const tourFacets = useTourFacetLocationCategory({})

  const categories = useMemo(() => {
    return CATEGORIES.filter(category => {
      if (category.show) {
        if (category.parameterName === 'link') {
          return !!category.parameterValue
        } else if (category.parameterName === 'categories') {
          return tourFacets.categories.has(category.parameterValue[0])
        } else if (category.parameterName === 'productTypes') {
          return tourFacets.productTypes.has(category.parameterValue)
        } else if (category.parameterName === 'tourStyles') {
          return tourFacets.tourStyles.has(category.parameterValue[0])
        } else if (TOURS_GROUP_TYPES_LIST.includes(category.parameterName as TOURS_GROUP_TYPE)) {
          return tourFacets.groupTypes.has(category.parameterName ?? '')
        }
        return true
      }
      return false
    })
  }, [tourFacets])

  return <VerticalSpacer gap={20}>
    <LayoutContainer>
      <Heading variant="heading2">
        {title ?? <>Explore <i>your</i> way</>}
      </Heading>
    </LayoutContainer>
    <FullWidthCarousel snap="start" itemsPerArrow={6}>
      {categories.map(category => {
        if (category.id === 'tailor_made_tours') {
          return <TourPageTailorMadeTourCategoryTile key={category.id} category={category} />
        } else {
          return <TourPageSearchTourCategoryTile key={category.id} category={category} />
        }
      })}
    </FullWidthCarousel>
  </VerticalSpacer>
}

export default React.memo(TourPageCategoriesCarousel)
